import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar } from '@/components/layout/Sidebar';
import { TopBar } from '@/components/layout/TopBar';
import { AmazonOauthBanner } from '@/components/AmazonOauthConnections';
import { cn } from '@/utils/cn';
import { parseWorkspaceScope } from '@/api/workspaceScope';

export function AppShell({ children }: { children: React.ReactNode }) {
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const prevPathnameRef = useRef('');
  // Chat and the AdPilot demo own their scroll regions; keep <main> from scrolling the page.
  const ownsPageScroll = /\/campaigns\/(chat|agent)\/?$/.test(location.pathname);
  const isFlipkart = parseWorkspaceScope(location.pathname)?.platform === 'flipkart';

  // Lock document scroll so only <main> scrolls (keeps TopBar fixed in view).
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      rootOverflow: root?.style.overflow ?? '',
      rootHeight: root?.style.height ?? '',
    };
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    if (root) {
      root.style.overflow = 'hidden';
      root.style.height = '100%';
    }
    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      if (root) {
        root.style.overflow = prev.rootOverflow;
        root.style.height = prev.rootHeight;
      }
    };
  }, []);

  // After RR7 commits the navigation (startTransition), reset <main> or honor a section hash.
  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!main) return;

    const pathnameChanged = prevPathnameRef.current !== location.pathname;
    prevPathnameRef.current = location.pathname;

    const id = location.hash.replace(/^#/, '');
    if (!id) {
      main.scrollTo(0, 0);
      return;
    }

    const behavior: ScrollBehavior = pathnameChanged ? 'auto' : 'smooth';
    let cancelled = false;
    let rafId = 0;
    let attempts = 0;
    const maxAttempts = 60;

    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior, block: 'start' });
        return;
      }
      if (attempts++ < maxAttempts) {
        rafId = requestAnimationFrame(tryScroll);
      }
    };

    tryScroll();
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [location.pathname, location.hash]);

  return (
    <div className="flex h-full min-h-0 w-full overflow-hidden bg-page">
      <Sidebar expanded={sidebarExpanded} onExpandedChange={setSidebarExpanded} />
      <div
        className={cn(
          'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden',
          'transition-[margin] duration-200 ease-out',
          // Collapsed rail is 68px; on hover nudge content right a bit (not full 220px width)
          sidebarExpanded ? 'ml-[108px]' : 'ml-[68px]'
        )}
      >
        <TopBar compact={ownsPageScroll} />
        {isFlipkart ? null : (
          <div className="shrink-0 px-6 pt-2">
            <AmazonOauthBanner />
          </div>
        )}
        <main
          ref={mainRef}
          className={cn(
            'custom-scrollbar min-h-0 flex-1',
            ownsPageScroll ? 'overflow-hidden' : 'overflow-y-auto'
          )}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
